import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import logger from '@/utils/logger'

// Lazy-loaded views
const LoginView = () => import('@/views/LoginView.vue')
const BreakGlassLoginView = () => import('@/views/BreakGlassLoginView.vue')
const StaffDashboardView = () => import('@/views/dashboard/StaffDashboardView.vue')
// SupportView removed — /support redirects to /configuration
const TicketsView = () => import('@/views/tickets/TicketsView.vue')
const TicketDetailView = () => import('@/views/tickets/TicketDetailView.vue')
// TicketingSettingsView — now embedded in ConfigurationCenterView
const ProjectsView = () => import('@/views/projects/ProjectsView.vue')
// ProjectSettingsView — now embedded in ConfigurationCenterView
const ProjectDetailView = () => import('@/views/projects/ProjectDetailView.vue')
const StaffTodosView = () => import('@/views/todos/StaffTodosView.vue')
const ClientProjectsView = () => import('@/views/client/ClientProjectsView.vue')
const ClientProjectDetailView = () => import('@/views/client/ClientProjectDetailView.vue')
const ClientTeamView = () => import('@/views/client/ClientTeamView.vue')
const ProcurementView = () => import('@/views/procurement/ProcurementView.vue')
const OrderDetailView = () => import('@/views/procurement/OrderDetailView.vue')
// ProcurementSettingsView — now embedded in ConfigurationCenterView
const ReportsView = () => import('@/views/reports/ReportsViewV2.vue')
const ClientsView = () => import('@/views/clients/ClientsView.vue')
const UsersView = () => import('@/views/users/UsersView.vue')
// SettingsView removed — /settings redirects to /configuration
const ConfigurationCenterView = () => import('@/views/settings/ConfigurationCenterView.vue')
const IntegrationsView = () => import('@/views/settings/IntegrationsView.vue')
const BackupsView = () => import('@/views/settings/BackupsView.vue')
const RbacEditorView = () => import('@/views/settings/RbacEditorView.vue')
const ActivityFeedView = () => import('@/views/ActivityFeedView.vue')
const DattoRmmView = () => import('@/views/DattoRmmView.vue')
const DownloadsView = () => import('@/views/DownloadsView.vue')
const PasswordsView = () => import('@/views/passwords/PasswordsView.vue')
const AssetsView = () => import('@/views/assets/AssetsView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')
const ReportBrandingView = () => import('@/views/reports/ReportBrandingView.vue')
const BrowserExtensionView = () => import('@/views/BrowserExtensionView.vue')
const BrowserSecurityView = () => import('@/views/BrowserSecurityView.vue')
// FreshdeskImportView removed — /settings/freshdesk-import redirects to /configuration
const CalendarManagementView = () => import('@/views/calendar/CalendarManagementView.vue')
const MyAccountView = () => import('@/views/MyAccountView.vue')
const EulaView = () => import('@/views/legal/EulaView.vue')
const PrivacyPolicyView = () => import('@/views/legal/PrivacyPolicyView.vue')
const BillingView = () => import('@/views/billing/BillingView.vue')
const SurveyView = () => import('@/views/public/SurveyView.vue')
const MyWorkView = () => import('@/views/MyWorkView.vue')
// HelpdeskDashboardView removed — absorbed into StaffDashboardView
const BurnLinkView = () => import('@/views/passwords/BurnLinkView.vue')
const KnowledgeBaseView = () => import('@/views/kb/KnowledgeBaseView.vue')
const ArticleDetailView = () => import('@/views/kb/ArticleDetailView.vue')
const ArticleEditorView = () => import('@/views/kb/ArticleEditorView.vue')
const HelpView = () => import('@/views/HelpView.vue')
const ChangeRequestsView = () => import('@/views/change-requests/ChangeRequestsView.vue')
const ChangeRequestDetailView = () => import('@/views/change-requests/ChangeRequestDetailView.vue')
const QbrGeneratorView = () => import('@/views/reports/QbrGeneratorView.vue')
const ReportBuilderView = () => import('@/views/reports/ReportBuilderView.vue')
const M365AlertsView = () => import('@/views/M365AlertsView.vue')
const ClientIntelligenceDashboard = () => import('@/views/ClientIntelligenceDashboard.vue')
const ClientPersonalDashboard = () => import('@/views/ClientPersonalDashboard.vue')
const ServiceCatalogView = () => import('@/views/clients/ServiceCatalogView.vue')
const SecurityPostureView = () => import('@/views/security/SecurityPostureView.vue')

const routes: RouteRecordRaw[] = [
  // Public routes
  { path: '/login', name: 'Login', component: LoginView, meta: { requiresAuth: false, layout: 'blank' } },
  { path: '/bglogin', alias: '/staff-login', name: 'StaffLogin', component: BreakGlassLoginView, meta: { requiresAuth: false, layout: 'blank' } },
  { path: '/extension', name: 'BrowserExtension', component: BrowserExtensionView, meta: { requiresAuth: false, layout: 'blank' } },
  { path: '/eula', name: 'EULA', component: EulaView, meta: { requiresAuth: false, layout: 'blank' } },
  { path: '/privacy-policy', name: 'PrivacyPolicy', component: PrivacyPolicyView, meta: { requiresAuth: false, layout: 'blank' } },
  { path: '/survey/:token', name: 'Survey', component: SurveyView, meta: { requiresAuth: false, layout: 'blank' } },
  { path: '/burn/:token', name: 'BurnLink', component: BurnLinkView, meta: { requiresAuth: false, layout: 'blank' } },
  
  // Main routes
  { path: '/', redirect: '/dashboard' },
  { path: '/helpdesk', redirect: '/dashboard' },
  { path: '/my-work', name: 'MyWork', component: MyWorkView, meta: { requiresAuth: true, staffOnly: true } },
  { path: '/my-devices', redirect: '/dashboard' },
  { path: '/dashboard', name: 'StaffDashboard', component: StaffDashboardView, meta: { requiresAuth: true, staffOnly: true } },
  { path: '/client/dashboard', name: 'ClientIntelDashboard', component: ClientIntelligenceDashboard, meta: { requiresAuth: true } },
  { path: '/dashboard/personal', name: 'ClientPersonalDashboard', component: ClientPersonalDashboard, meta: { requiresAuth: true } },
  { path: '/assets', name: 'Assets', component: AssetsView, meta: { requiresAuth: true, portalFeature: 'assets' } },
  { path: '/support', redirect: '/configuration' },
  // Tickets
  { path: '/tickets', name: 'Tickets', component: TicketsView, meta: { requiresAuth: true, permission: 'tickets.view' } },
  { path: '/tickets/settings', redirect: '/configuration/ticketing' },
  { path: '/tickets/:id', name: 'TicketDetail', component: TicketDetailView, meta: { requiresAuth: true, permission: 'tickets.view' } },
  
  // Change Requests
  { path: '/change-requests', name: 'ChangeRequests', component: ChangeRequestsView, meta: { requiresAuth: true, permission: 'change_requests.view' } },
  { path: '/change-requests/:id', name: 'ChangeRequestDetail', component: ChangeRequestDetailView, meta: { requiresAuth: true, permission: 'change_requests.view' } },

  // Knowledge Base
  { path: '/knowledge-base', name: 'KnowledgeBase', component: KnowledgeBaseView, meta: { requiresAuth: true, permission: 'kb.view' } },
  { path: '/knowledge-base/new', name: 'KBArticleCreate', component: ArticleEditorView, meta: { requiresAuth: true, permission: 'kb.create' } },
  { path: '/knowledge-base/:id/edit', name: 'KBArticleEdit', component: ArticleEditorView, meta: { requiresAuth: true, permission: 'kb.edit' } },
  { path: '/knowledge-base/:id', name: 'KBArticleDetail', component: ArticleDetailView, meta: { requiresAuth: true, permission: 'kb.view' } },

  // Projects
  { path: '/projects', name: 'Projects', component: ProjectsView, meta: { requiresAuth: true, permission: 'projects.view' } },
  { path: '/projects/settings', redirect: '/configuration/projects' },
  { path: '/projects/:id', name: 'ProjectDetail', component: ProjectDetailView, meta: { requiresAuth: true, permission: 'projects.view' } },
  
  // Todos (staff only)
  { path: '/todos', name: 'Todos', component: StaffTodosView, meta: { requiresAuth: true, staffOnly: true, permission: 'todos.view' } },

  // Client Portal - Projects
  { path: '/client/projects', name: 'ClientProjects', component: ClientProjectsView, meta: { requiresAuth: true, portalFeature: 'projects' } },
  { path: '/client/projects/:id', name: 'ClientProjectDetail', component: ClientProjectDetailView, meta: { requiresAuth: true, portalFeature: 'projects' } },

  // Client Portal - Service Catalog
  { path: '/client/services', name: 'ServiceCatalog', component: ServiceCatalogView, meta: { requiresAuth: true, portalFeature: 'service_catalog' } },

  // Client Portal - Team (Client Admin only — enforced by SideNav + backend scoping)
  { path: '/client/team', name: 'ClientTeam', component: ClientTeamView, meta: { requiresAuth: true, permission: 'users.view' } },
  
  // Procurement (staff only)
  { path: '/procurement', name: 'Procurement', component: ProcurementView, meta: { requiresAuth: true, staffOnly: true, permission: 'orders.view' } },
  { path: '/procurement/settings', redirect: '/configuration/procurement' },
  { path: '/procurement/:id', name: 'OrderDetail', component: OrderDetailView, meta: { requiresAuth: true, staffOnly: true, permission: 'orders.view' } },
  
  // Reports
  { path: '/reports', name: 'Reports', component: ReportsView, meta: { requiresAuth: true, permission: 'reports.view', portalFeature: 'reports' } },
  { path: '/reports/branding', name: 'ReportBranding', component: ReportBrandingView, meta: { requiresAuth: true, staffOnly: true, permission: 'reports.branding' } },
  { path: '/reports/qbr', name: 'QbrGenerator', component: QbrGeneratorView, meta: { requiresAuth: true, staffOnly: true, permission: 'reports.view', title: 'QBR Generator' } },
  { path: '/reports/builder', name: 'ReportBuilder', component: ReportBuilderView, meta: { requiresAuth: true, staffOnly: true, permission: 'reports.builder', title: 'Report Builder' } },
  { path: '/reports/builder/:id', name: 'ReportBuilderEdit', component: ReportBuilderView, meta: { requiresAuth: true, staffOnly: true, permission: 'reports.builder', title: 'Edit Report' } },
  
  // Billing (Client Admin and Super Admin only)
  { path: '/billing', name: 'Billing', component: BillingView, meta: { requiresAuth: true, portalFeature: 'billing' } },
  
  // Downloads
  { path: '/downloads', name: 'Downloads', component: DownloadsView, meta: { requiresAuth: true } },
  
  // Password Manager
  { path: '/passwords', name: 'Password Manager', component: PasswordsView, meta: { requiresAuth: true, portalFeature: 'vault' } },
  
  // User Account
  { path: '/account', name: 'MyAccount', component: MyAccountView, meta: { requiresAuth: true } },

  // Help
  { path: '/help', name: 'Help', component: HelpView, meta: { requiresAuth: true, title: 'Help' } },
  
  // Admin routes (staff only)
  { path: '/clients', name: 'Clients', component: ClientsView, meta: { requiresAuth: true, staffOnly: true, permission: 'clients.view' } },
  { path: '/users', name: 'Users', component: UsersView, meta: { requiresAuth: true, staffOnly: true, permission: 'users.view' } },
  { path: '/settings', redirect: '/configuration' },

  // Configuration Center — consolidated admin hub
  { path: '/configuration', name: 'ConfigurationCenter', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.general' } },
  { path: '/configuration/integrations', name: 'ConfigIntegrations', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.general' } },
  { path: '/configuration/integrations/datto', name: 'ConfigDattoRmm', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'datto.view' } },
  { path: '/configuration/ai', name: 'ConfigAI', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.integrations' } },
  { path: '/configuration/roles', name: 'ConfigRoles', component: ConfigurationCenterView, meta: { requiresAuth: true, superAdminOnly: true, permission: 'settings.rbac' } },
  { path: '/configuration/backups', name: 'ConfigBackups', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.view' } },
  { path: '/configuration/notifications', name: 'ConfigNotifications', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.general' } },
  { path: '/configuration/calendar', name: 'ConfigCalendar', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.general' } },
  { path: '/configuration/announcements', name: 'ConfigAnnouncements', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.general' } },
  { path: '/configuration/advanced', name: 'ConfigAdvanced', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.general' } },
  { path: '/configuration/ticketing', name: 'ConfigTicketing', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.ticketing' } },
  { path: '/configuration/projects', name: 'ConfigProjects', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.projects' } },
  { path: '/configuration/procurement', name: 'ConfigProcurement', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.procurement' } },
  { path: '/configuration/assets', name: 'ConfigAssets', component: ConfigurationCenterView, meta: { requiresAuth: true, staffOnly: true, permission: 'settings.general' } },
  { path: '/configuration/billing', name: 'ConfigBilling', component: ConfigurationCenterView, meta: { requiresAuth: true, superAdminOnly: true, permission: 'settings.general' } },
  { path: '/configuration/etl-export', name: 'ConfigEtlExport', component: ConfigurationCenterView, meta: { requiresAuth: true, superAdminOnly: true, permission: 'settings.general' } },

  // Redirects for old standalone routes → Configuration Center
  { path: '/admin/configuration', redirect: '/configuration' },
  { path: '/integrations', redirect: '/configuration/integrations' },
  { path: '/rbac', redirect: '/configuration/roles' },
  { path: '/settings/rbac', redirect: '/configuration/roles' },
  { path: '/backups', redirect: '/configuration/backups' },
  { path: '/datto-rmm', redirect: '/configuration/integrations/datto' },

  { path: '/settings/freshdesk-import', redirect: '/configuration' },
  { path: '/activity', name: 'ActivityFeed', component: ActivityFeedView, meta: { requiresAuth: true, staffOnly: true, permission: 'activity.view' } },
  { path: '/calendar', name: 'CalendarManagement', component: CalendarManagementView, meta: { requiresAuth: true, staffOnly: true, permission: 'calendar.view' } },
  { path: '/m365-alerts', name: 'M365Alerts', component: M365AlertsView, meta: { requiresAuth: true, staffOnly: true, permission: 'm365_alerts.view' } },
  { path: '/browser-security', name: 'BrowserSecurity', component: BrowserSecurityView, meta: { requiresAuth: true, portalFeature: 'browser_security' } },
  { path: '/security/posture', name: 'SecurityPosture', component: SecurityPostureView, meta: { requiresAuth: true, staffOnly: true, permission: 'security.view' } },
  
  // 404
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFoundView, meta: { layout: 'blank' } },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, savedPosition) => savedPosition || { top: 0 }
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  const requiresAuth = to.meta.requiresAuth !== false

  const getDefaultClientRoute = () => (authStore.isClientUser && !authStore.isClientAdmin) ? 'ClientPersonalDashboard' : 'ClientIntelDashboard'

  // Only fetch user if route requires auth and user hasn't been loaded yet
  if (requiresAuth && authStore.isLoading) await authStore.fetchUser()
  
  // Redirect to login if auth required but not logged in
  if (requiresAuth && !authStore.isAuthenticated) {
    return next({ name: 'Login', query: { redirect: to.fullPath } })
  }
  
  // Redirect to appropriate page if already logged in and trying to access login
  if ((to.name === 'Login' || to.name === 'StaffLogin') && authStore.isAuthenticated) {
    // Staff → Staff Dashboard, Client Admin → Client Dashboard, Restricted client roles → Personal Dashboard
    return next({ name: authStore.isStaff ? 'StaffDashboard' : getDefaultClientRoute() })
  }
  
  // Restricted client role restrictions (Client Employee, Client Read-Only, etc.) - redirect to Personal Dashboard
  if (authStore.isClientUser && !authStore.isClientAdmin) {
    const restrictedRoutes = ['StaffDashboard', 'Projects', 'ProjectDetail', 'ClientProjects', 'ClientProjectDetail', 'Reports', 'Billing', 'Assets', 'Support', 'ClientIntelDashboard', 'BrowserSecurity']
    if (restrictedRoutes.includes(to.name as string)) {
      return next({ name: 'ClientPersonalDashboard' })
    }
  }
  
  // Check staff-only routes
  if (to.meta.staffOnly && !authStore.isStaff) {
    return next({ name: getDefaultClientRoute() })
  }

  // Check super admin only routes
  if (to.meta.superAdminOnly && !authStore.isSuperAdmin) {
    return next({ name: getDefaultClientRoute() })
  }

  // Check permission-based routes
  if (to.meta.permission && typeof to.meta.permission === 'string' && !authStore.hasPermission(to.meta.permission)) {
    return next({ name: getDefaultClientRoute() })
  }

  // Check portal feature gates (client users only — staff always passes)
  if (to.meta.portalFeature && typeof to.meta.portalFeature === 'string' && !authStore.hasPortalFeature(to.meta.portalFeature)) {
    return next({ name: getDefaultClientRoute() })
  }

  next()
})

// Handle chunk load failures (happens after deployments when cache is stale)
router.onError((error) => {
  if (error.message.includes('Failed to fetch dynamically imported module') || 
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk')) {
    logger.warn('[Router] Chunk load failed, reloading page...', { message: error.message })
    window.location.reload()
  }
})

export default router
